import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  HandCoins, ArrowRight, Loader2, AlertCircle, RefreshCw,
  MapPin, Users, Clock, FileText, Info, ClipboardList,
  PlayCircle, MessageSquare, Ban, StickyNote, CheckCircle2,
  RotateCcw, ShieldCheck, Send,
} from "lucide-react";
import {
  FUNDING_STATUS_LABEL, FUNDING_STATUS_TONE, FUNDING_EVENT_LABEL,
  formatSAR, formatArabicDateTime,
} from "../donor/fundingLabels";

const CONTRIBUTION_LABEL = {FINANCIAL:"مساهمة مالية",IN_KIND:"مساهمة عينية",SPONSORSHIP:"رعاية",OTHER:"أخرى"};
const BENEFICIARY_LABEL = {STUDENTS:"طلاب",ASSOCIATIONS:"جمعيات",BOTH:"طلاب وجمعيات"};
const SCOPE_LABEL = {KINGDOM_WIDE:"على مستوى المملكة",REGION:"منطقة محددة",CITY:"مدينة محددة"};
const DURATION_UNIT_LABEL = {WEEKS:"أسبوع",MONTHS:"شهر"};
function labelValue(m,v){return v?(m[v]||v):"—";}

export default function AdminFundingRequestDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [row, setRow] = useState(null);
  const [events, setEvents] = useState(null);
  const [plans, setPlans] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  // Action forms
  const [reasonOpen, setReasonOpen] = useState(null); // 'request_information'|'decline'
  const [reasonText, setReasonText] = useState("");
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [planForm, setPlanForm] = useState({template_key:"", template_version:"", content_json:'{"summary":"","budget":"","milestones":""}'});
  const [editPlanContent, setEditPlanContent] = useState({});
  const [editPlanOpen, setEditPlanOpen] = useState(null); // plan_id being edited
  const [returnOpen, setReturnOpen] = useState(null); // plan_id
  const [returnReason, setReturnReason] = useState("");

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [rReq, rEv, rPlans, rTpl] = await Promise.all([
        api.get(`/funding/requests/${id}`),
        api.get(`/funding/requests/${id}/events`),
        api.get(`/funding/requests/${id}/plans`),
        api.get(`/funding/admin/plan-templates`).catch(()=>({data:{templates:[]}})),
      ]);
      setRow(rReq.data.request);
      setEvents(rEv.data.events || []);
      setPlans(rPlans.data.plans || []);
      setTemplates(rTpl.data.templates || []);
      if (rTpl.data.templates?.length && !planForm.template_key) {
        setPlanForm(prev => ({...prev, template_key: rTpl.data.templates[0].key, template_version: String(rTpl.data.templates[0].version)}));
      }
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  async function doAction(action, extra={}) {
    if (busy) return;
    setBusy(action);
    try {
      await api.post(`/funding/admin/requests/${row.id}/action`, {
        action, _expected_lock_version: row.lock_version, ...extra,
      });
      toast.success("تم تنفيذ الإجراء");
      setReasonOpen(null); setReasonText("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  }
  async function addNote() {
    if (busy || !noteText.trim()) return;
    setBusy("note");
    try {
      await api.post(`/funding/admin/requests/${row.id}/note`, { message: noteText.trim() });
      toast.success("أُضيفت الملاحظة الإدارية");
      setNoteOpen(false); setNoteText("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  }
  async function createPlan() {
    if (busy) return;
    let contentObj;
    try { contentObj = JSON.parse(planForm.content_json || "{}"); }
    catch { toast.error("محتوى الخطة يجب أن يكون JSON صحيحاً"); return; }
    setBusy("create_plan");
    try {
      await api.post(`/funding/admin/requests/${row.id}/plans`, {
        template_key: planForm.template_key, template_version: Number(planForm.template_version),
        content: contentObj,
      });
      toast.success("أُنشئت مسودة الخطة");
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  }
  async function editPlanSave(plan) {
    if (busy) return;
    setBusy(`edit_${plan.id}`);
    try {
      await api.patch(`/funding/admin/plans/${plan.id}`, {
        _expected_lock_version: plan.lock_version, content: editPlanContent,
      });
      toast.success("تم حفظ التعديلات");
      setEditPlanOpen(null); setEditPlanContent({});
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  }
  async function planReady(plan) {
    if (busy) return;
    setBusy(`ready_${plan.id}`);
    try {
      await api.post(`/funding/admin/plans/${plan.id}/ready`, { _expected_lock_version: plan.lock_version });
      toast.success("الخطة أصبحت جاهزة");
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  }
  async function planReturn(plan) {
    if (busy || !returnReason.trim()) return;
    setBusy(`return_${plan.id}`);
    try {
      await api.post(`/funding/admin/plans/${plan.id}/return`, {
        _expected_lock_version: plan.lock_version, reason: returnReason.trim(),
      });
      toast.success("أُعيدت الخطة إلى المُعِد");
      setReturnOpen(null); setReturnReason("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  }
  async function planApprove(plan) {
    if (busy) return;
    setBusy(`approve_${plan.id}`);
    try {
      await api.post(`/funding/admin/plans/${plan.id}/approve`, { _expected_lock_version: plan.lock_version });
      toast.success("تم اعتماد الخطة");
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  }
  async function planPublish(plan) {
    if (busy) return;
    setBusy(`publish_${plan.id}`);
    try {
      await api.post(`/funding/admin/plans/${plan.id}/publish`, { _expected_lock_version: plan.lock_version });
      toast.success("تم نشر الخطة");
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  }

  const currentPlan = plans.find(p => p.status === "DRAFT" || p.status === "READY") || plans[plans.length - 1];
  const canStartReview = row?.status === "SUBMITTED";
  const canRequestInfo = row?.status === "UNDER_REVIEW";
  const canStartPlan = row?.status === "UNDER_REVIEW";
  const canDecline = row && ["SUBMITTED","UNDER_REVIEW","INFORMATION_REQUIRED"].includes(row.status);
  const canCreatePlan = row?.status === "PLAN_IN_PREPARATION" && !plans.some(p => p.status === "DRAFT" || p.status === "READY");

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="admin-details-page">
        <button type="button" onClick={()=>navigate("/dashboard/admin/funding/requests")}
          className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-3" data-testid="admin-details-back">
          <ArrowRight className="h-3 w-3" /> العودة إلى قائمة الطلبات
        </button>

        {loading && <div className="flex items-center justify-center py-24" data-testid="admin-details-loading"><Loader2 className="h-7 w-7 animate-spin text-indigo-700" /></div>}
        {!loading && error && (
          <div className="p-4 rounded-2xl bg-rose-50 border border-rose-200"><AlertCircle className="h-6 w-6 text-rose-700" /><p className="text-sm text-rose-800 mt-1">{error}</p><Button variant="outline" onClick={load} className="mt-3"><RefreshCw className="h-4 w-4 ms-2" /> إعادة</Button></div>
        )}
        {!loading && !error && row && (
          <>
            <div className="mb-6 flex items-start gap-3">
              <div className="h-11 w-11 rounded-xl bg-indigo-50 text-indigo-700 grid place-items-center shrink-0"><HandCoins className="h-5 w-5" strokeWidth={1.75} /></div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-bold uppercase tracking-widest text-indigo-700" data-testid="admin-details-request-number">{row.request_number || "مسودة"}</div>
                <h1 className="mt-1 text-2xl font-heading font-extrabold text-slate-900 break-words">{row.funding_goal || "—"}</h1>
                <div className="mt-2"><StatusPill status={row.status} /></div>
              </div>
            </div>

            {/* Summary */}
            <div className="grid gap-4 sm:grid-cols-2 mb-6">
              <SummaryCard icon={FileText} label="نوع المساهمة" value={labelValue(CONTRIBUTION_LABEL,row.contribution_type)} testId="admin-sum-contribution" />
              <SummaryCard icon={HandCoins} label="القيمة التقديرية" value={formatSAR(row.estimated_amount_or_value)} testId="admin-sum-amount" />
              <SummaryCard icon={Users} label="المستفيد" value={<BeneficiarySummary row={row}/>} testId="admin-sum-beneficiary" />
              <SummaryCard icon={MapPin} label="النطاق" value={<ScopeSummary row={row}/>} testId="admin-sum-scope" />
              <SummaryCard icon={Clock} label="مدة التنفيذ" value={<DurationSummary tep={row.target_execution_period}/>} testId="admin-sum-duration" />
              <SummaryCard icon={Info} label="ملاحظات إدارية" value={row._admin_internal_note_count || 0} testId="admin-sum-note-count" />
            </div>

            {row.request_details && (
              <div className="mb-6 p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid="admin-details-block-request">
                <h3 className="font-heading font-bold text-slate-900 mb-2">تفاصيل الطلب</h3>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{row.request_details}</p>
              </div>
            )}

            {/* Admin actions */}
            <div className="mb-6 p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid="admin-actions-block">
              <h3 className="font-heading font-bold text-slate-900 mb-3">إجراءات الإدارة</h3>
              <div className="flex flex-wrap gap-2">
                {canStartReview && (
                  <Button onClick={()=>doAction("start_review")} disabled={!!busy} data-testid="admin-btn-start-review" className="bg-indigo-700 hover:bg-indigo-800">
                    {busy==="start_review" ? <Loader2 className="h-4 w-4 animate-spin ms-2"/> : <PlayCircle className="h-4 w-4 ms-2"/>} بدء المراجعة
                  </Button>
                )}
                {canRequestInfo && (
                  <Button variant="outline" onClick={()=>setReasonOpen("request_information")} disabled={!!busy} data-testid="admin-btn-request-info">
                    <MessageSquare className="h-4 w-4 ms-2"/> طلب معلومات إضافية
                  </Button>
                )}
                {canStartPlan && (
                  <Button onClick={()=>doAction("start_plan")} disabled={!!busy} data-testid="admin-btn-start-plan" className="bg-teal-700 hover:bg-teal-800">
                    {busy==="start_plan" ? <Loader2 className="h-4 w-4 animate-spin ms-2"/> : <ClipboardList className="h-4 w-4 ms-2"/>} بدء إعداد الخطة
                  </Button>
                )}
                {canDecline && (
                  <Button variant="outline" onClick={()=>setReasonOpen("decline")} disabled={!!busy} className="text-rose-700 border-rose-300 hover:bg-rose-50" data-testid="admin-btn-decline">
                    <Ban className="h-4 w-4 ms-2"/> رفض الطلب
                  </Button>
                )}
                <Button variant="outline" onClick={()=>setNoteOpen(true)} disabled={!!busy} data-testid="admin-btn-open-note">
                  <StickyNote className="h-4 w-4 ms-2"/> ملاحظة إدارية داخلية
                </Button>
              </div>

              {reasonOpen && (
                <div className="mt-4 p-4 rounded-xl bg-amber-50 border border-amber-200" data-testid="admin-reason-form">
                  <Label className="text-sm font-semibold">{reasonOpen === "decline" ? "سبب الرفض" : "سبب طلب المعلومات"} <span className="text-rose-600">*</span></Label>
                  <Textarea rows={3} value={reasonText} onChange={(e)=>setReasonText(e.target.value)} className="mt-1" data-testid="admin-reason-input" />
                  <div className="flex gap-2 mt-3">
                    <Button onClick={()=>doAction(reasonOpen,{reason:reasonText.trim()})} disabled={!!busy || !reasonText.trim()} data-testid="admin-reason-submit" className="bg-indigo-700 hover:bg-indigo-800">
                      {busy===reasonOpen ? <Loader2 className="h-4 w-4 animate-spin ms-2"/> : <Send className="h-4 w-4 ms-2"/>} إرسال
                    </Button>
                    <Button variant="outline" onClick={()=>{setReasonOpen(null); setReasonText("");}}>إلغاء</Button>
                  </div>
                </div>
              )}
              {noteOpen && (
                <div className="mt-4 p-4 rounded-xl bg-slate-50 border border-slate-200" data-testid="admin-note-form">
                  <Label className="text-sm font-semibold">ملاحظة داخلية (تظهر للإدارة فقط)</Label>
                  <Textarea rows={3} value={noteText} onChange={(e)=>setNoteText(e.target.value)} className="mt-1" data-testid="admin-note-input"/>
                  <div className="flex gap-2 mt-3">
                    <Button onClick={addNote} disabled={!!busy || !noteText.trim()} data-testid="admin-note-submit" className="bg-slate-700 hover:bg-slate-800">
                      {busy==="note" ? <Loader2 className="h-4 w-4 animate-spin ms-2"/> : <Send className="h-4 w-4 ms-2"/>} حفظ
                    </Button>
                    <Button variant="outline" onClick={()=>{setNoteOpen(false); setNoteText("");}}>إلغاء</Button>
                  </div>
                </div>
              )}
            </div>

            {/* Plan section */}
            <div className="mb-6 p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid="admin-plan-block">
              <h3 className="font-heading font-bold text-slate-900 mb-3 flex items-center gap-2"><ClipboardList className="h-5 w-5 text-indigo-700"/> خطة التمويل</h3>

              {canCreatePlan && (
                <div className="p-4 rounded-xl bg-indigo-50/40 border border-indigo-200 mb-4" data-testid="admin-plan-create-form">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label className="text-xs">قالب الخطة</Label>
                      <select className="w-full mt-1 px-3 py-2 rounded-md border border-slate-300 text-sm" value={planForm.template_key} onChange={(e)=>{
                        const t = templates.find(x=>x.key===e.target.value);
                        setPlanForm(prev=>({...prev, template_key:e.target.value, template_version: t?String(t.version):""}));
                      }} data-testid="admin-plan-template-key">
                        <option value="">-- اختر --</option>
                        {templates.map(t => <option key={`${t.key}:${t.version}`} value={t.key}>{t.title || t.key} · v{t.version}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">إصدار القالب</Label>
                      <Input value={planForm.template_version} onChange={(e)=>setPlanForm(prev=>({...prev,template_version:e.target.value}))} data-testid="admin-plan-template-version" className="mt-1" />
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label className="text-xs">محتوى الخطة (JSON)</Label>
                    <Textarea rows={6} value={planForm.content_json} onChange={(e)=>setPlanForm(prev=>({...prev,content_json:e.target.value}))} data-testid="admin-plan-content-json" className="mt-1 font-mono text-xs" />
                  </div>
                  <Button onClick={createPlan} disabled={!!busy || !planForm.template_key} className="mt-3 bg-indigo-700 hover:bg-indigo-800" data-testid="admin-plan-create-submit">
                    {busy==="create_plan" ? <Loader2 className="h-4 w-4 animate-spin ms-2"/> : <ClipboardList className="h-4 w-4 ms-2"/>} إنشاء مسودة الخطة
                  </Button>
                </div>
              )}

              {plans.length === 0 && !canCreatePlan && (
                <p className="text-sm text-slate-500" data-testid="admin-plan-empty">لا توجد خطة بعد. ابدأ بمراجعة الطلب ثم بدء إعداد الخطة.</p>
              )}

              {plans.map(plan => (
                <div key={plan.id} className="p-4 rounded-xl border border-slate-200 mb-3" data-testid={`admin-plan-${plan.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <div className="text-sm font-bold">إصدار #{plan.version_number} · <span data-testid={`admin-plan-status-${plan.id}`}>{plan.status}</span></div>
                      <div className="text-xs text-slate-500">قالب {plan.template_key} v{plan.template_version}</div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {plan.status === "DRAFT" && (
                        <>
                          <Button size="sm" variant="outline" onClick={()=>{setEditPlanOpen(plan.id); setEditPlanContent({...(plan.content||{})});}} data-testid={`admin-btn-edit-plan-${plan.id}`}>
                            تحرير
                          </Button>
                          <Button size="sm" onClick={()=>planReady(plan)} disabled={!!busy} className="bg-teal-700 hover:bg-teal-800" data-testid={`admin-btn-plan-ready-${plan.id}`}>
                            {busy===`ready_${plan.id}` ? <Loader2 className="h-3 w-3 animate-spin"/> : <CheckCircle2 className="h-3 w-3"/>} تعليم كجاهزة
                          </Button>
                        </>
                      )}
                      {plan.status === "READY" && (
                        <>
                          <Button size="sm" variant="outline" onClick={()=>setReturnOpen(plan.id)} data-testid={`admin-btn-plan-return-open-${plan.id}`}>
                            <RotateCcw className="h-3 w-3"/> إعادة
                          </Button>
                          {!plan.approved_by ? (
                            <Button size="sm" onClick={()=>planApprove(plan)} disabled={!!busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid={`admin-btn-plan-approve-${plan.id}`}>
                              {busy===`approve_${plan.id}` ? <Loader2 className="h-3 w-3 animate-spin"/> : <ShieldCheck className="h-3 w-3"/>} اعتماد
                            </Button>
                          ) : (
                            <Button size="sm" onClick={()=>planPublish(plan)} disabled={!!busy} className="bg-indigo-700 hover:bg-indigo-800" data-testid={`admin-btn-plan-publish-${plan.id}`}>
                              {busy===`publish_${plan.id}` ? <Loader2 className="h-3 w-3 animate-spin"/> : <Send className="h-3 w-3"/>} نشر
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {editPlanOpen === plan.id ? (
                    <div className="mt-2 space-y-2" data-testid={`admin-edit-plan-form-${plan.id}`}>
                      {Object.entries(editPlanContent).map(([k, v]) => (
                        <div key={k}>
                          <Label className="text-xs">{k}</Label>
                          <Textarea rows={2} value={String(v ?? "")} onChange={(e)=>setEditPlanContent(prev=>({...prev,[k]:e.target.value}))} data-testid={`admin-edit-plan-input-${plan.id}-${k}`} className="mt-1 text-sm" />
                        </div>
                      ))}
                      <div className="flex gap-2 mt-2">
                        <Button size="sm" onClick={()=>editPlanSave(plan)} disabled={!!busy} data-testid={`admin-edit-plan-save-${plan.id}`} className="bg-teal-700 hover:bg-teal-800">حفظ</Button>
                        <Button size="sm" variant="outline" onClick={()=>{setEditPlanOpen(null); setEditPlanContent({});}}>إلغاء</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-slate-600 space-y-1 mt-2">
                      {Object.entries(plan.content || {}).map(([k, v]) => (
                        <div key={k}><span className="font-semibold">{k}:</span> {typeof v === "string" ? v : JSON.stringify(v)}</div>
                      ))}
                    </div>
                  )}
                  {returnOpen === plan.id && (
                    <div className="mt-3 p-3 rounded-xl bg-amber-50 border border-amber-200" data-testid={`admin-return-form-${plan.id}`}>
                      <Label className="text-xs">سبب الإعادة <span className="text-rose-600">*</span></Label>
                      <Textarea rows={2} value={returnReason} onChange={(e)=>setReturnReason(e.target.value)} data-testid={`admin-return-reason-${plan.id}`} className="mt-1"/>
                      <div className="flex gap-2 mt-2">
                        <Button size="sm" onClick={()=>planReturn(plan)} disabled={!!busy || !returnReason.trim()} data-testid={`admin-return-submit-${plan.id}`}>إرسال</Button>
                        <Button size="sm" variant="outline" onClick={()=>{setReturnOpen(null); setReturnReason("");}}>إلغاء</Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Timeline */}
            <div className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid="admin-events-timeline">
              <h3 className="font-heading font-bold text-slate-900 mb-4">سجل الحالة والأحداث</h3>
              {events && events.length ? (
                <ol className="relative border-s-2 border-cream-200 space-y-5 ps-5">
                  {events.map(e => (
                    <li key={e.id} className="relative" data-testid={`admin-event-${e.id}`}>
                      <span className="absolute -start-[27px] top-1.5 h-3.5 w-3.5 rounded-full bg-indigo-600 border-2 border-white shadow"></span>
                      <div className="text-sm font-semibold text-slate-900">{FUNDING_EVENT_LABEL[e.event_type] || e.event_type} <span className="text-xs font-normal text-slate-500">· {e.actor_role}</span></div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {formatArabicDateTime(e.created_at)}
                        {e.from_status && e.to_status && (<> · {FUNDING_STATUS_LABEL[e.from_status] || e.from_status} → {FUNDING_STATUS_LABEL[e.to_status] || e.to_status}</>)}
                      </div>
                      {e.message && <div className="mt-1.5 text-sm text-slate-700 whitespace-pre-wrap">{e.message}</div>}
                    </li>
                  ))}
                </ol>
              ) : <p className="text-sm text-slate-500">لا أحداث بعد.</p>}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

function StatusPill({ status }) {
  const label = FUNDING_STATUS_LABEL[status] || status || "—";
  const tone = FUNDING_STATUS_TONE[status] || "bg-slate-100 text-slate-700 border-slate-200";
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-xs font-semibold ${tone}`} data-testid={`admin-details-status-${status || "unknown"}`}>{label}</span>;
}
function SummaryCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid={testId}>
      <div className="flex items-center gap-2 text-xs text-slate-500 font-semibold">
        <Icon className="h-4 w-4 text-slate-400" strokeWidth={1.75} /><span>{label}</span>
      </div>
      <div className="mt-1.5 text-sm text-slate-900 font-semibold break-words">{value ?? "—"}</div>
    </div>
  );
}
function BeneficiarySummary({ row }) {
  const parts = [labelValue(BENEFICIARY_LABEL, row.beneficiary_type)];
  if (row.students_count != null) parts.push(`طلاب: ${row.students_count}`);
  if (row.associations_count != null) parts.push(`جمعيات: ${row.associations_count}`);
  return <span>{parts.join(" · ")}</span>;
}
function ScopeSummary({ row }) {
  const parts = [labelValue(SCOPE_LABEL, row.geographic_scope)];
  if (row.region) parts.push(row.region);
  if (row.city) parts.push(row.city);
  return <span>{parts.join(" · ")}</span>;
}
function DurationSummary({ tep }) {
  if (!tep || !tep.duration_value) return "—";
  const unit = DURATION_UNIT_LABEL[tep.duration_unit] || tep.duration_unit;
  return <span>{tep.duration_value} {unit}</span>;
}
